import React from 'react';

import { TFunction } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Fields, FieldType, useFormFieldValue } from '@/components/Form';
import AIPraiseMessage from '@/components/LyearnAI/components/Editor/components/AIPraiseMessage';
import RichTextViewer from '@/components/RichTextViewer';
import ParticipantSelection from '@/modules/1on1s/components/Setup1on1Modal/ParticipantSelection';
import { hideImageSeparator } from '@/modules/corporate/modules/channel/views/ChannelDetails/components/MessageInput/styles';
import { TextUseCaseEnum } from '@/types/schema';

import LearningContent from './components/LearningContent';
import RelatedSkillOrMindset from './components/RelatedSkillsOrMindsets';
import { InitialFormValueType } from './types';

export const CREATE_FEEDBACK_INITIAL_VALUES: InitialFormValueType = {
  receiverUserId: [],
  feedbackMessage: '',
  visibleToManager: false,
  learningContentIds: [],
  skillRatings: [],
  mindsetRatings: [],
  medias: [],
  mentions: [],
};

const EMPTY_ARRAY: never[] = [];

export const getCreateFeedbackFormInitialValues = (userIds?: string[]): InitialFormValueType =>
  userIds
    ? {
        ...CREATE_FEEDBACK_INITIAL_VALUES,
        receiverUserId: userIds,
      }
    : CREATE_FEEDBACK_INITIAL_VALUES;

export const validateMeetingForm = (t: TFunction, values: InitialFormValueType): any => {
  const errors: any = {};

  if (!values.receiverUserId.length) {
    errors.receiverUserId = t('Feedback:CreateFeedback.Receiver.EmptyError');
  }

  if (!values.feedbackMessage) {
    errors.feedbackMessage = t('Feedback:CreateFeedback.Message.EmptyError');
  }

  return errors;
};

export const getFields = ({
  t,
  isEditing,
}: {
  t: TFunction;
  isEditing?: boolean;
}): Fields<InitialFormValueType> => ({
  receiverUserId: {
    name: 'receiverUserId',
    // TODO: using FieldType.Custom since isEditing seems constant
    type: FieldType.Custom,
    label: t('Feedback:CreateFeedback.Receiver.Title'),
    Component: ({ value, onChange, error, touched, setFieldValue }) => {
      const onParticipantChange = (userIds: string[]) => {
        onChange(userIds);
        setFieldValue('learningContentIds', []);
        setFieldValue('skillRatings', []);
        setFieldValue('mindsetRatings', []);
      };
      return (
        <ParticipantSelection
          disabled={isEditing}
          error={touched && error}
          placeholder={t('Feedback:CreateFeedback.Receiver.Placeholder')}
          value={value}
          onChange={onParticipantChange}
        />
      );
    },
  },
  feedbackMessage: {
    name: 'feedbackMessage',
    type: FieldType.SlowCustom,
    label: t('Common:Routes.Feedback'),
    Component: ({ value, touched, error, onChange, setFieldValue, formik }) => {
      const templateId = useFormFieldValue('templateId');
      const medias = useFormFieldValue('medias');

      return (
        <AIPraiseMessage
          key={templateId}
          error={Boolean(touched && error)}
          medias={medias || EMPTY_ARRAY}
          placeholder={t('Feedback:CreateFeedback.Message.Placeholder')}
          setFieldValue={setFieldValue}
          textUseCase={TextUseCaseEnum.UserFeedback}
          topic={value}
          value={value}
          onChange={onChange}
          onTopicError={() => {
            formik.setFieldTouched('feedbackMessage', true);
            formik.setFieldError(
              'feedbackMessage',
              'Write a word or two for AI to work its magic!',
            );
          }}
        />
      );
    },
  },
  visibleToManager: {
    name: 'visibleToManager',
    type: FieldType.Switch,
    label: t('Feedback:CreateFeedback.Visible.Title'),
  },
  learningContentIds: {
    name: 'learningContentIds',
    type: FieldType.Custom,
    Component: ({ value, onChange }) => {
      return <LearningContent value={value ?? EMPTY_ARRAY} onChange={onChange} />;
    },
  },
  skillRatings: {
    name: 'skillRatings',
    type: FieldType.Custom,
    Component: ({ value, onChange }) => {
      const receiverUserId = useFormFieldValue('receiverUserId');

      return (
        <RelatedSkillOrMindset
          skillRatings={value ?? EMPTY_ARRAY}
          userId={receiverUserId?.[0]}
          onChange={onChange as TSFixMe}
        />
      );
    },
  },
  mindsetRatings: {
    name: 'mindsetRatings',
    type: FieldType.Custom,
    Component: ({ value, onChange }) => {
      const receiverUserId = useFormFieldValue('receiverUserId');

      return (
        <RelatedSkillOrMindset
          isMindset
          skillRatings={value ?? EMPTY_ARRAY}
          userId={receiverUserId?.[0]}
          onChange={onChange as TSFixMe}
        />
      );
    },
  },
  requestMessage: {
    name: 'requestMessage',
    type: FieldType.Custom,
    Component: ({ value }) => {
      return (
        <RichTextViewer
          className={cn('mb-20 rounded-8 bg-bg-secondary px-16 py-12', hideImageSeparator)}
          color="text-text-paragraph"
          html={`"${value}"`}
          variant="body-short-01"
        />
      );
    },
  },
});

export const getSectionLayout = ({
  t,
  isRequestPresent,
}: {
  t: TFunction;
  isRequestPresent: boolean;
}) => [
  {
    id: 'GeneralDetails',
    layout: isRequestPresent
      ? ['receiverUserId', 'requestMessage', 'feedbackMessage', 'visibleToManager']
      : ['receiverUserId', 'feedbackMessage', 'visibleToManager'],
  },
  {
    id: 'SkillRatings',
    layout: ['skillRatings'],
    name: t('Feedback:CreateFeedback.Skills.Title'),
  },
  {
    id: 'MindsetRatings',
    layout: ['mindsetRatings'],
    name: t('Feedback:CreateFeedback.Mindsets.Title'),
  },
  {
    id: 'LearningContent',
    layout: ['learningContentIds'],
    name: t('Feedback:CreateFeedback.LearningContent.Title'),
  },
];
